import React, { useEffect, useState } from 'react';
import { ClipboardList, FileText, PieChart } from 'lucide-react';
import ClaimsWorkspace from './ClaimsWorkspace';
import EvvReconciliation from '../patient/caseManagement/EvvReconciliation';
import BillingBottomNav from '../layout/BillingBottomNav';
import { useMobileDetection } from '../../hooks/useMobileUtils';
import { useFeature } from '../../hooks/useFeature';
import { useCanPerform } from '../../hooks/useCanPerform';
import { usePermission } from '../../hooks/usePermission';
import { EVV_READ } from '../patient/caseManagement/evv/evvUi';

type BillingTab = 'Claims' | 'EVV Reconciliation' | 'Reports';

interface BillingHomeProps {
  activeSubsection?: string | null;
  setActiveSubsection?: (subsection: string) => void;
  sidebarCollapsed?: boolean;
}

const BillingHome: React.FC<BillingHomeProps> = ({
  activeSubsection,
  setActiveSubsection,
  sidebarCollapsed = true
}) => {
  const isMobile = useMobileDetection();
  // Phase 3: the Reports placeholder is hidden unless the billingReports flag is on
  const billingReportsEnabled = useFeature('billingReports');
  // PRD §3.2: Coordinators reconcile EVV but do not see claims
  const canReadClaims = useCanPerform('read:claims');
  const canReadEvv = usePermission(EVV_READ);
  const homeTab: BillingTab = canReadClaims || !canReadEvv ? 'Claims' : 'EVV Reconciliation';
  const [activeTab, setActiveTab] = useState<BillingTab>(
    activeSubsection === 'EVV Reconciliation' || activeSubsection === 'Reports'
      ? activeSubsection
      : homeTab
  );
  useEffect(() => {
    if (activeSubsection === 'Claims' || activeSubsection === 'EVV Reconciliation' || activeSubsection === 'Reports') {
      setActiveTab(activeSubsection);
    } else if (!activeSubsection) {
      setActiveTab(homeTab);
    }
  }, [activeSubsection, homeTab]);

  // A deep link to a tab the user cannot open falls back to their home tab
  const allowed = (tab: BillingTab) => (tab === 'Claims' || tab === 'Reports' ? canReadClaims : canReadEvv)
    && (tab !== 'Reports' || billingReportsEnabled);
  const visibleTab: BillingTab = allowed(activeTab) ? activeTab : homeTab;

  const selectTab = (tab: BillingTab) => {
    setActiveTab(tab);
    setActiveSubsection?.(tab);
  };

  const renderTabContent = (tab: BillingTab, expanded: boolean, toggle: () => void) => {
    if (tab === 'Claims') {
      return <ClaimsWorkspace onGoToReconciliation={() => selectTab('EVV Reconciliation')} />;
    }
    if (tab === 'EVV Reconciliation') {
      return <EvvReconciliation expanded={expanded} toggleExpanded={toggle} />;
    }
    return (
      <div className="p-8 text-center">
        <PieChart className="mx-auto mb-3 text-slate-400" size={32} />
        <h1 className="text-lg font-semibold text-slate-800">Billing Reports</h1>
        <p className="mt-1 text-sm text-slate-500">Reports will appear here when available.</p>
      </div>
    );
  };

  const tabs = [
    ...(canReadClaims ? [{ name: 'Claims' as const, icon: FileText }] : []),
    ...(canReadEvv ? [{ name: 'EVV Reconciliation' as const, icon: ClipboardList }] : []),
    ...(billingReportsEnabled && canReadClaims ? [{ name: 'Reports' as const, icon: PieChart }] : [])
  ];

  return (
    <div className={isMobile ? 'p-0' : 'p-4'}>
      {isMobile ? (
        <div className="space-y-3 pt-3 pb-20 px-3">
          <div className="rounded-lg shadow-md overflow-hidden">
            {renderTabContent(visibleTab, true, () => undefined)}
          </div>
          <BillingBottomNav
            activeTab={visibleTab}
            onTabChange={selectTab}
            sidebarCollapsed={sidebarCollapsed}
          />
        </div>
      ) : (
        <div className="mb-4">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-2 pt-2 pb-0">
              <div className="flex space-x-1" role="tablist" aria-label="Billing">
                {tabs.map(({ name, icon: Icon }) => (
                  <button
                    key={name}
                    type="button"
                    role="tab"
                    aria-selected={visibleTab === name}
                    onClick={() => selectTab(name)}
                    className={`px-4 py-2 text-sm font-medium rounded-t-md transition-all duration-200 flex items-center space-x-1.5 ${
                      visibleTab === name
                        ? 'bg-slate-800 text-white'
                        : 'bg-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-800'
                    }`}
                  >
                    <Icon size={16} />
                    {name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div key={visibleTab} role="tabpanel">
            {renderTabContent(visibleTab, true, () => undefined)}
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingHome;